import { useMemo } from "react";
import { ArrowLeft, Calendar, MessageSquare, User, Stethoscope, CheckCircle, Clock } from "lucide-react";
import PropTypes from "prop-types";
import { cpf } from "../../../utils/formats";
import "./../css/confirmationPage.css";

const ConfirmationPage = ({
  patient,
  procedures,
  doctor,
  details,
  onConfirm,
  onBack,
}) => {
  const getProcPrice = (proc) => {
    const byId =
      doctor?.prices?.[proc.procedure_id ?? proc.id] ??
      doctor?.prices?.[proc.id];

    if (typeof byId === "number" && !isNaN(byId)) return byId;

    return Number(proc.value ?? proc.cost_price ?? proc.price ?? 0);
  };

  const allInsurance = procedures.every((p) => p.payment?.type === "insurance");

  const totalPrice = useMemo(
    () =>
      procedures.reduce((sum, proc) => {
        if (proc.payment?.type === "insurance") return sum;
        return sum + getProcPrice(proc);
      }, 0),
    [procedures]
  );

  const formatCurrency = (v = 0) => `R$ ${Number(v).toFixed(2).replace(".", ",")}`;

  const formattedDate = useMemo(() => {
    if (!details.date) return "";
    return new Date(details.date + "T00:00:00").toLocaleDateString("pt-BR", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }, [details.date]);


  return (
    <div className="page-container confirmation-page">
      {/* ---------- Cabeçalho ---------- */}
      <div className="page-header">
        <button className="back-button" onClick={onBack}>
          <ArrowLeft size={20} />
          <span>Voltar</span>
        </button>
        <h1>Confirmação de Agendamento</h1>
      </div>

      <div className="confirmation-content">
        {/* ---------- Resumo ---------- */}
        <section className="summary-section">
          {/* Card agrupado com as três primeiras informações */}
          <div className="summary-card-group">
            {/* Paciente */}
            <div className="summary-info-line">
              <User size={18} />
              <div>
                <h4>Paciente</h4>
                <p>{patient.name}</p>
                <p>CPF: {cpf(patient.cpf)}</p>
              </div>
            </div>

            {/* Profissional */}
            <div className="summary-info-line">
              <Stethoscope size={18} />
              <div>
                <h4>Profissional</h4>
                <p>{doctor.name}</p>
              </div>
            </div>

            {/* Data & Hora */}
            <div className="summary-info-line date-time-line">
              <Calendar size={18} />
              <div>
                <h4>Data</h4>
                <p>{formattedDate}</p>
              </div>
              <Clock size={18} />
              <div>
                <h4>Hora</h4>
                <p>{details.time}</p>
              </div>
            </div>
          </div>

          {/* Observações - permanece como card separado */}
          {details.comments && (
            <div className="summary-card">
              <MessageSquare size={18} />
              <div>
                <h4>Observações</h4>
                <p>{details.comments}</p>
              </div>
            </div>
          )}
        </section>

        {/* ---------- Procedimentos ---------- */}
        <section className="procedures-section">
          <h3>
            <CheckCircle size={20} style={{ marginRight: 6 }} />
            Procedimentos
          </h3>

          <ul className="procedures-list">
            {procedures.map((proc) => {
              const price = getProcPrice(proc);
              return (
                <li key={proc.id}>
                  <span>{proc.name}: </span>
                  <span className="procedure-price">
                    {proc.payment?.type === "insurance"
                      ? "Convênio"
                      : formatCurrency(price)}
                  </span>
                </li>
              );
            })}
          </ul>

          <div className="total-price-container">
            <span>Valor&nbsp;Total:</span>
            <span className="total-price-value">
              {allInsurance ? "Convênio" : formatCurrency(totalPrice)}
            </span>
          </div>
        </section>

        {/* ---------- Ações ---------- */}
        <div className="form-actions">
          <button type="button" className="cancel-button" onClick={onBack}>
            <ArrowLeft size={20} />
            Voltar e Editar
          </button>
          <button
            type="button"
            className="submit-button"
            onClick={() => onConfirm({ patient, procedures, doctor, details })}>
            <Calendar size={20} className="me-2" />
            Confirmar Agendamento
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─────────── validação de props ─────────── */
ConfirmationPage.propTypes = {
  patient: PropTypes.shape({
    id: PropTypes.any.isRequired,
    name: PropTypes.string.isRequired,
    cpf: PropTypes.string.isRequired,
  }).isRequired,
  procedures: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.any.isRequired,
      name: PropTypes.string.isRequired,
      code: PropTypes.string,
      procedure_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      payment: PropTypes.shape({
        type: PropTypes.oneOf(["patient", "insurance"]).isRequired,
        insurance: PropTypes.string,
      }).isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      cost_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
  doctor: PropTypes.shape({
    id: PropTypes.any.isRequired,
    name: PropTypes.string.isRequired,
    specialty: PropTypes.string,
    prices: PropTypes.object, // { [procedureId]: number }
  }).isRequired,
  details: PropTypes.shape({
    date: PropTypes.string.isRequired,
    time: PropTypes.string.isRequired,
    comments: PropTypes.string,
  }).isRequired,
  onConfirm: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default ConfirmationPage;
